import { usePagination } from '@faststore/sdk'
import React, { useContext, useEffect, useState } from 'react'
import { HeaderSizeContext } from 'src/Layout'
import useScrollPosition from 'src/hooks/useScrollPosition'
import type {
  CollectionPageQueryQuery,
  Filter_FacetsFragment,
} from '@generated/graphql'
import { isStorageAvailable } from 'src/utils/isStorageAvailable'
import { useFilterContext } from 'src/contexts/FilterContext/filter-context'

import { useOrderedFacets } from './useOrderedFacets'
import { useTotalCount } from './useTotalCount'
import { useGalleryQuery } from './useGalleryQuery'
import './product-gallery.scss'
import { ProductGalleryNull } from './ProductGalleryNull'
import { ProductGalleryAbove } from './ProductGalleryAbove'
import ProductGalleryBelow from './ProductGalleryBelow'
import { ProductGalleryJsonLd } from './ProductGalleryJsonLd'

const HIDDEN_FILTERS = [
  'colecao',
  'altura-aprox',
  'aro',
  'comprimento-aprox',
  'espessura-aprox',
  'largura-aprox',
  'malha',
  'tamanho',
]

interface Props {
  title: string
  cmsHome?: CollectionPageQueryQuery['cmsHome']
  description?: string
  term: string | null
  base: string
  visibleFilters?: {
    allItems: Array<{
      filterKey: string | null
    } | null> | null
  } | null
  sendInsiderEvent?: boolean
  defaultFacets?: Array<{
    key: string
    value: string
  }>
  cmsData?: Array<{
    name: string
    data: unknown
  }>
}

interface IFilterVisibleFacets {
  visibleFilters?: {
    allItems: Array<{
      filterKey: string | null
    } | null> | null
  } | null

  orderedFacets: Filter_FacetsFragment[]
}

interface AnchorOffset {
  top: number
}

const ADJUST_OFFSET = 20

function filterVisibleFacets({
  visibleFilters,
  orderedFacets,
}: IFilterVisibleFacets) {
  const visibleFiltersArray = visibleFilters?.allItems?.map((e) => e?.filterKey)

  const visibleFacets = orderedFacets?.filter((e) => {
    return visibleFiltersArray?.includes(e.key)
  })

  return visibleFacets.length ? visibleFacets : orderedFacets
}

export function scrollToTop(id: string) {
  if (id && id !== '') {
    const elToScroll = document?.getElementById(id)

    if (elToScroll) {
      window.scroll({ top: 0 })
      let sizeTop = 150

      const headerHeight = document
        ?.querySelector('.header-content-wrapper')
        ?.getBoundingClientRect().height

      const topBarHeight = document
        ?.querySelector('.topbar-wrapper-vivara')
        ?.getBoundingClientRect().height

      if (headerHeight && topBarHeight) {
        sizeTop = headerHeight + topBarHeight
      }

      const y = elToScroll?.getBoundingClientRect().top

      setTimeout(() => {
        window.scrollTo({ top: y - sizeTop, behavior: 'smooth' })
      }, 1000)
    }
  }
}

function ProductGallery({
  title,
  term,
  base,
  cmsHome,
  description = '',
  visibleFilters,
  sendInsiderEvent,
  defaultFacets,
  cmsData,
}: Props) {
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
  const [insiderEventSent, setInsiderEventSent] = useState(false)
  const [anchorOffset, setAnchorOffset] = useState<AnchorOffset>()
  const [stickyClass, setStickyClass] = useState('')
  const scrollPosition = useScrollPosition()

  const headerSizeContext = useContext(HeaderSizeContext)
  const { selectedFacets, setSelectedFacets, sort } = useFilterContext()
  const { data, isValidating } = useGalleryQuery(selectedFacets, sort)
  const totalCount = useTotalCount(data)
  const orderedFacets = useOrderedFacets(data)
  const { next, prev } = usePagination(totalCount)
  const storedLayout = localStorage.getItem('layout')
  const [layout, setLayout] = useState(storedLayout ?? 'grid')
  const isLayoutList = layout === 'list'

  const filteredFacets =
    cmsData === undefined && window?.location?.pathname?.includes('/colecao/')
      ? filterVisibleFacets({ visibleFilters, orderedFacets }).filter(
          (facet) => !HIDDEN_FILTERS.includes(facet?.key)
        )
      : filterVisibleFacets({ visibleFilters, orderedFacets })

  useEffect(() => {
    localStorage.setItem('layout', layout)
  }, [layout])

  const handleChangeLayout = (value: string) => {
    setLayout(value)
  }

  const [lastScroll, setLastScroll] = useState<number>(0)
  const [stickyFilterScrollClass, setStickyFilterScrollClass] =
    useState<string>('')

  const isSearchPage = title === 'Search Results'

  const idToTop = 'scroll-to-top'

  const productList = data?.search.products.edges

  useEffect(() => {
    if (!sendInsiderEvent || productList === undefined || insiderEventSent) {
      return
    }

    const dataproduct = productList.map((productInfo) => {
      const breadcrumbItems = productInfo?.node?.breadcrumbList?.itemListElement
      const breadcrumbName = breadcrumbItems?.map((item) => item.name) || []
      const imageItems = productInfo?.node?.image

      const urlImage = imageItems?.map((item) => item.url) || []

      const taxonomy = breadcrumbName

      taxonomy?.shift()
      taxonomy?.pop()

      return {
        items: [
          {
            id: productInfo?.node?.id,
            name: productInfo?.node?.name,
            taxonomy: taxonomy || [],
            currency: 'BRL',
            unit_price: productInfo?.node?.offers?.offers[0]?.price,
            unit_sale_price: productInfo?.node?.offers?.offers[0]?.price,
            url: `${window?.location?.origin}/${productInfo?.node?.slug}/p`,
            product_image_url: urlImage,
          },
        ],
      }
    })

    window.insider_object = window.insider_object || {}
    window.insider_object.listing = {
      dataproduct,
    }

    setInsiderEventSent(true)
  }, [sendInsiderEvent, productList, insiderEventSent])

  useEffect(() => {
    if (headerSizeContext?.headerSize && !anchorOffset) {
      setAnchorOffset({ top: -(headerSizeContext.headerSize + ADJUST_OFFSET) })
    }
  }, [headerSizeContext, anchorOffset])

  const scrollToElementWithInterval = (element: string) => {
    const urlParams = new URLSearchParams(window.location.href)
    const urlIsFiltered = urlParams.has('filtered')

    const intervalExistsElement = setInterval(() => {
      if (urlIsFiltered) {
        const listElement = document?.querySelectorAll(element)

        if (listElement?.length) {
          if (isStorageAvailable()) {
            const storageClickedProductId = document.getElementById(
              sessionStorage.getItem('clickedProductId') ?? ''
            )

            idToTop && !storageClickedProductId && scrollToTop(idToTop)
          }

          clearInterval(intervalExistsElement)

          return true
        }
      }

      return false
    }, 200)
  }

  useEffect(() => {
    scrollToElementWithInterval('ul.product-grid li')
  }, [])

  const stickyFilter = () => {
    const currentScrollTop =
      window?.scrollY || document?.documentElement.scrollTop

    scrollPosition > 200 ? setStickyClass('-sticky-filter') : setStickyClass('')

    if (lastScroll > currentScrollTop) {
      setStickyFilterScrollClass('scroll-up-filter')
    }

    if (lastScroll < currentScrollTop) {
      setStickyFilterScrollClass('scroll-down-filter')
    }

    setLastScroll(currentScrollTop)
  }

  useEffect(() => {
    window.addEventListener('scroll', stickyFilter)

    return () => {
      window.removeEventListener('scroll', stickyFilter)
    }
  })

  return (
    <>
      {anchorOffset && (
        <div
          className={`product-listing / grid-content-full ${
            stickyClass ? 'spaceSticky' : ''
          }`}
        >
          {!isValidating && data?.search?.products?.edges?.length === 0 ? (
            <ProductGalleryNull
              cmsHome={cmsHome}
              idToTop={idToTop}
              stickyClass={`${stickyClass} ${stickyFilterScrollClass}`}
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
              filteredFacets={filteredFacets}
              term={term}
              base={base}
              selectedFacets={selectedFacets}
              setSelectedFacets={setSelectedFacets}
            />
          ) : (
            <div
              id={`${idToTop ?? idToTop}`}
              className="product-listing__content-grid / grid-content"
            >
              <ProductGalleryAbove
                anchorOffset={anchorOffset}
                stickyClass={`${stickyClass} ${stickyFilterScrollClass}`}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
                filteredFacets={filteredFacets}
                term={term}
                base={base}
                layout={layout}
                handleChangeLayout={handleChangeLayout}
                isSearchPage={isSearchPage}
                totalCount={totalCount}
                defaultFacets={defaultFacets}
                selectedFacets={selectedFacets}
                setSelectedFacets={setSelectedFacets}
                sort={sort}
              />
              <ProductGalleryBelow
                prev={prev}
                next={next}
                data={data}
                title={title}
                isLayoutList={isLayoutList}
                setIsButtonLoading={setIsButtonLoading}
                isButtonLoading={isButtonLoading}
                selectedFacets={selectedFacets}
              />
            </div>
          )}
        </div>
      )}
      <ProductGalleryJsonLd
        title={title}
        description={description}
        base={base}
        products={productList}
      />
    </>
  )
}

export default ProductGallery
